import React, { useState } from 'react';
import '../styles/RiskInfoPage.css';

interface RiskInfoPageProps {
  riskType: 'ascvd' | 'tumor';
  onBack: () => void;
}

interface BaseSection {
  title: string;
  content: string;
  detail?: string;
  showAnimation?: boolean;
  factors?: Array<{
    label: string;
    status: string;
    value: string;
    target: string;
  }>;
  actions?: Array<{
    icon: string;
    title: string;
    desc: string;
  }>;
  treatments?: Array<{
    type: string;
    title: string;
    desc: string;
    recommended: boolean;
  }>;
  nodule?: {
    size: string;
    type: string;
    location: string;
    risk: string;
  };
  schedule?: Array<{
    time: string;
    action: string;
    purpose: string;
  }>;
}

// 血液流动动画组件
const BloodFlowAnimation: React.FC<{ riskLevel: 'normal' | 'high' }> = ({ riskLevel }) => {
  return (
    <div className="blood-flow-container">
      <div className="vessel-wrapper">
        <div className={`blood-vessel ${riskLevel}`}>
          <div className="vessel-wall">
            <div className="plaque-buildup" />
          </div>
          <div className="blood-flow">
            <div className="blood-cell cell-1" />
            <div className="blood-cell cell-2" />
            <div className="blood-cell cell-3" />
            <div className="blood-cell cell-4" />
          </div>
        </div>
        <div className="flow-indicator">
          <div className={`flow-speed ${riskLevel}`}>
            <span className="speed-label">
              {riskLevel === 'normal' ? '正常血流' : '血流受阻'}
            </span>
          </div>
        </div>
      </div>
      <div className="animation-description">
        <div className="desc-item">
          <div className="desc-icon normal" />
          <span>正常血管：血流顺畅</span>
        </div>
        <div className="desc-item">
          <div className="desc-icon blocked" />
          <span>动脉硬化：血流受阻，增加心血管风险</span>
        </div>
      </div>
    </div>
  );
};

// 风险评估卡片组件
const RiskAssessmentCard: React.FC<{ riskType: string }> = ({ riskType }) => {
  if (riskType === 'ascvd') {
    return (
      <div className="risk-assessment-card">
        {/* 风险等级标签 */}
        <div className="risk-header-clean">
          <div className="risk-badge-clean medium">
            <span className="badge-dot"></span>
            <span className="badge-text">中等风险</span>
          </div>
          <span className="assessment-subtitle-clean">基于最新健康数据分析</span>
        </div>

        {/* 核心风险数据 */}
        <div className="risk-core-data">
          <div className="main-risk-score">
            <span className="score-number">12%</span>
            <span className="score-period">10年发病风险</span>
          </div>
          <p className="risk-explanation">基于您当前的健康指标，10年内发生心血管事件的风险为12%</p>
        </div>

        {/* 状态对比 */}
        <div className="status-comparison">
          <div className="status-item current">
            <div className="status-header">
              <span className="status-label">当前状态</span>
              <span className="status-indicator warning">血脂偏高</span>
            </div>
            <span className="status-value">12% 风险</span>
          </div>
          
          <div className="comparison-arrow">→</div>
          
          <div className="status-item improved">
            <div className="status-header">
              <span className="status-label">改善后</span>
              <span className="status-indicator success">生活方式干预</span>
            </div>
            <span className="status-value">≤ 6% 风险</span>
          </div>
        </div>

        {/* 改善提示 */}
        <div className="improvement-tip">
          <span className="tip-icon">✨</span>
          <span className="tip-text">通过改善生活方式，可将风险降至6%以下</span>
        </div>
      </div>
    );
  }
  return null;
};

const RiskInfoPage: React.FC<RiskInfoPageProps> = ({ riskType, onBack }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'prevention' | 'treatment'>('overview');

  const getContent = (): {
    title: string;
    subtitle: string;
    tabs: {
      [key: string]: {
        title: string;
        sections: BaseSection[];
      };
    };
  } => {
    if (riskType === 'ascvd') {
      return {
        title: '心血管(ASCVD)风险',
        subtitle: '动脉粥样硬化性心血管疾病',
        tabs: {
          overview: {
            title: '风险概述',
            sections: [
              {
                title: '什么是ASCVD？',
                content: 'ASCVD（动脉粥样硬化性心血管疾病）是由于动脉内壁脂质沉积和炎症反应导致的血管病变。',
                detail: '当胆固醇等脂质在动脉壁上积聚形成斑块时，会使血管变窄、变硬，影响血液正常流动，从而增加心脏病和中风的风险。',
                showAnimation: true
              },
              {
                title: 'ASCVD可能引起的疾病',
                content: 'ASCVD是全球致残致死的主要原因，可引发多种严重的心血管并发症。',
                detail: '根据世界卫生组织统计，心血管疾病每年导致全球约1790万人死亡，占全球死亡总数的31%。在中国，心血管疾病每年导致约400万人死亡，致残人数超过1000万人。',
                actions: [
                  { icon: '💔', title: '冠心病/心肌梗死', desc: '冠状动脉狭窄导致心肌缺血坏死' },
                  { icon: '🧠', title: '缺血性脑卒中', desc: '脑血管堵塞引起脑组织缺血' },
                  { icon: '🦵', title: '外周动脉疾病', desc: '四肢血管狭窄导致循环障碍' },
                  { icon: '🫀', title: '心力衰竭', desc: '心脏泵血功能受损引起循环衰竭' }
                ]
              },
              {
                title: '主要影响因素',
                content: '血脂异常是ASCVD的主要危险因素，特别是LDL胆固醇（坏胆固醇）升高。',
                factors: [
                  { label: 'LDL胆固醇', status: 'high', value: '3.8 mmol/L', target: '< 2.6' },
                  { label: '血压', status: 'normal', value: '125/80 mmHg', target: '< 130/80' },
                  { label: '血糖', status: 'normal', value: '5.2 mmol/L', target: '< 6.1' }
                ]
              }
            ]
          },
          prevention: {
            title: '预防措施',
            sections: [
              {
                title: '生活方式干预',
                content: '健康的生活方式是预防ASCVD最有效的方法。',
                actions: [
                  { icon: '🥗', title: '健康饮食', desc: '减少饱和脂肪，增加膳食纤维' },
                  { icon: '🏃‍♂️', title: '规律运动', desc: '每周至少150分钟中等强度运动' },
                  { icon: '🚭', title: '戒烟限酒', desc: '完全戒烟，限制饮酒量' },
                  { icon: '⚖️', title: '控制体重', desc: '维持健康的BMI范围' }
                ]
              }
            ]
          },
          treatment: {
            title: '治疗方案',
            sections: [
              {
                title: '个性化治疗',
                content: '根据您的风险等级，建议采用以下治疗策略：',
                treatments: [
                  { type: 'medication', title: '他汀类药物', desc: '降低LDL胆固醇，稳定斑块', recommended: true },
                  { type: 'lifestyle', title: '生活方式调整', desc: '饮食、运动、戒烟的综合管理', recommended: true },
                  { type: 'monitoring', title: '定期监测', desc: '每3-6个月复查血脂和相关指标', recommended: true }
                ]
              }
            ]
          }
        }
      };
    } else {
      return {
        title: '肺结节监测',
        subtitle: '肺部小结节随访指南',
        tabs: {
          overview: {
            title: '结节概述',
            sections: [
              {
                title: '什么是肺结节？',
                content: '肺结节是在胸部CT扫描中发现的圆形或类圆形病灶，直径通常小于3厘米。',
                detail: '大多数肺结节是良性的，但需要根据大小、形态、密度等特征评估恶性风险。'
              },
              {
                title: '您的结节特征',
                content: '基于最新的影像学检查结果：',
                nodule: {
                  size: '6mm',
                  type: '磨玻璃结节',
                  location: '右上叶',
                  risk: '低风险'
                }
              }
            ]
          },
          prevention: {
            title: '预防建议',
            sections: [
              {
                title: '生活方式调整',
                content: '通过健康的生活方式降低肺癌风险。',
                actions: [
                  { icon: '🚭', title: '戒烟', desc: '戒烟是最重要的预防措施' },
                  { icon: '💨', title: '避免污染', desc: '减少空气污染和有害物质接触' },
                  { icon: '🥬', title: '健康饮食', desc: '多食用富含抗氧化剂的食物' },
                  { icon: '🏃‍♀️', title: '适度运动', desc: '增强免疫力和肺功能' }
                ]
              }
            ]
          },
          treatment: {
            title: '随访方案',
            sections: [
              {
                title: '监测计划',
                content: '根据结节特征制定个性化随访方案：',
                schedule: [
                  { time: '3个月后', action: 'CT复查', purpose: '观察结节变化' },
                  { time: '6个月后', action: 'CT复查', purpose: '评估生长速度' },
                  { time: '12个月后', action: 'CT复查', purpose: '长期随访评估' }
                ]
              }
            ]
          }
        }
      };
    }
  };

  const content = getContent();

  return (
    <div className="risk-info-page">
      {/* 导航栏 */}
      <div className="risk-info-header">
        <div className="header-nav">
          <button className="back-btn" onClick={onBack}>
            <span className="back-icon">‹</span>
            <span>返回</span>
          </button>
        </div>
        <div className="header-titles">
          <h1 className="info-title">{content.title}</h1>
          <p className="info-subtitle">{content.subtitle}</p>
        </div>
      </div>

      {/* 风险评估卡片 */}
      <div className="assessment-section">
        <RiskAssessmentCard riskType={riskType} />
      </div>



      {/* 标签页导航 */}
      <div className="tab-navigation">
        {Object.entries(content.tabs).map(([key, tab]) => (
          <button
            key={key}
            className={`tab-btn ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key as any)}
          >
            {tab.title}
          </button>
        ))}
      </div>

      {/* 内容区域 */}
      <div className="risk-info-content">
        {content.tabs[activeTab].sections.map((section, index) => (
          <div key={index} className="info-section">
            <h3 className="section-title">{section.title}</h3>
            <p className="section-content">{section.content}</p>
            
            {section.detail && (
              <div className="section-detail">
                <p>{section.detail}</p>
              </div>
            )}

            {section.showAnimation && riskType === 'ascvd' && (
              <div className="inline-animation">
                <div className="animation-header">
                  <h4 className="animation-title">血管状态对比</h4>
                  <p className="animation-subtitle">了解动脉硬化如何影响血液流动</p>
                </div>
                <BloodFlowAnimation riskLevel="high" />
              </div>
            )}

            {section.factors && (
              <div className="factors-grid">
                {section.factors.map((factor, idx) => (
                  <div key={idx} className={`factor-card ${factor.status}`}>
                    <div className="factor-header">
                      <span className="factor-label">{factor.label}</span>
                      <span className={`factor-status ${factor.status}`}>
                        {factor.status === 'high' ? '偏高' : '正常'}
                      </span>
                    </div>
                    <div className="factor-values">
                      <span className="current-value">{factor.value}</span>
                      <span className="target-value">目标: {factor.target}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {section.actions && (
              <div className="actions-grid">
                {section.actions.map((action, idx) => (
                  <div key={idx} className="action-card">
                    <div className="action-icon">{action.icon}</div>
                    <div className="action-content">
                      <h4 className="action-title">{action.title}</h4>
                      <p className="action-desc">{action.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {section.treatments && (
              <div className="treatments-list">
                {section.treatments.map((treatment, idx) => (
                  <div key={idx} className={`treatment-item ${treatment.recommended ? 'recommended' : ''}`}>
                    <div className="treatment-header">
                      <h4 className="treatment-title">{treatment.title}</h4>
                      {treatment.recommended && <span className="recommended-badge">推荐</span>}
                    </div>
                    <p className="treatment-desc">{treatment.desc}</p>
                  </div>
                ))}
              </div>
            )}

            {section.nodule && (
              <div className="nodule-info">
                <div className="nodule-detail">
                  <div className="detail-item">
                    <span className="label">大小：</span>
                    <span className="value">{section.nodule.size}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">类型：</span>
                    <span className="value">{section.nodule.type}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">位置：</span>
                    <span className="value">{section.nodule.location}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">风险：</span>
                    <span className={`value risk-${section.nodule.risk.includes('低') ? 'low' : 'high'}`}>
                      {section.nodule.risk}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {section.schedule && (
              <div className="schedule-timeline">
                {section.schedule.map((item, idx) => (
                  <div key={idx} className="timeline-item">
                    <div className="timeline-marker" />
                    <div className="timeline-content">
                      <div className="timeline-time">{item.time}</div>
                      <div className="timeline-action">{item.action}</div>
                      <div className="timeline-purpose">{item.purpose}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 底部免责声明 */}
      <div className="risk-info-footer">
        <div className="footer-content">
          <p className="disclaimer">
            ※ 本内容仅供健康科普参考，具体诊疗请咨询专业医生
          </p>
          <p className="update-info">
            最后更新：2024年7月23日
          </p>
        </div>
      </div>
    </div>
  );
};

export default RiskInfoPage; 